/**
 * The one error the concurrency contract (architecture §B.10, decision T-12) needs a name for.
 *
 * A conflict is not a transport failure and not a validation failure: it means the caller acted
 * on a belief the server no longer holds. The only correct response is re-read-and-retry, and
 * that is a decision for the UI — never a silent retry in the client, which would overwrite
 * whatever the other operator just wrote.
 */
export type ConflictKind =
  /** 428 — the mutation carried no precondition at all. A client bug, surfaced loudly. */
  | 'precondition-required'
  /** 412 — the precondition was stale: the resource moved between the read and the write. */
  | 'precondition-failed'
  /** 409 — a domain conflict (`ConflictReason` in the contract), e.g. the timeline head moved. */
  | 'conflict';

export interface ConflictErrorInit {
  readonly kind: ConflictKind;
  readonly status: number;
  readonly method: string;
  readonly url: string;
  /** The resource whose ETag was (or should have been) quoted back. */
  readonly resourceUrl: string;
  /** Sent as If-Match, when one was known. */
  readonly sentEtag?: string | undefined;
  /** The server's current ETag, when it names one — the value a retry should carry. */
  readonly currentEtag?: string | undefined;
  /** Parsed response body, if it was JSON; the raw text otherwise. */
  readonly body?: unknown;
}

const MESSAGES: Record<ConflictKind, string> = {
  'precondition-required':
    'The service requires an If-Match precondition on this mutation and the client had no ETag ' +
    'for the resource. Read it before writing it (architecture §B.10).',
  'precondition-failed':
    'The resource changed between the read and this write. Re-read it and let the user decide ' +
    'what to do with the newer state — never retry silently.',
  conflict:
    'The service rejected this write as conflicting with the current state of the resource.',
};

export class ConflictError extends Error {
  override readonly name = 'ConflictError';
  readonly kind: ConflictKind;
  readonly status: number;
  readonly method: string;
  readonly url: string;
  readonly resourceUrl: string;
  readonly sentEtag: string | undefined;
  readonly currentEtag: string | undefined;
  readonly body: unknown;

  constructor(init: ConflictErrorInit) {
    super(`${init.method} ${init.url} → ${String(init.status)}. ${MESSAGES[init.kind]}`);
    this.kind = init.kind;
    this.status = init.status;
    this.method = init.method;
    this.url = init.url;
    this.resourceUrl = init.resourceUrl;
    this.sentEtag = init.sentEtag;
    this.currentEtag = init.currentEtag;
    this.body = init.body;
  }
}

/**
 * `instanceof` across a workspace with one module instance is reliable, but the console also
 * carries errors through TanStack Query caches and error boundaries; a structural check keeps
 * those call sites honest even where the class identity has been lost.
 */
export function isConflictError(error: unknown): error is ConflictError {
  return error instanceof ConflictError;
}
